import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { usePersistentUserChoices } from '@livekit/components-react'
import { MeetingPrejoinRoomDataDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { PrejoinControlBar } from 'modules/meetings/components/PrejoinControlBar'
import { PrejoinVideoContainer } from 'modules/meetings/components/PrejoinVideoContainer'
import { PrivacyModal } from 'modules/meetings/components/PrivacyModal'
import { PrejoinSkeleton } from 'modules/meetings/components/Skeletons/PrejoinSkeleton'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useVirtualBackgroundSettings } from 'modules/meetings/lib/hooks/useVirtualBackgroundSettings'
import { formatDateToString, formatTimeToString } from 'modules/meetings/lib/utils/dates'

const MeetingPrejoinRoomPage = () => {
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false)
  const { employeeId } = useParams<{ employeeId: string }>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { loading: pageDetailsLoading, meetingId } = useMeeting()
  const { userChoices } = usePersistentUserChoices()
  const { backgroundType: initialBackgroundType } = useVirtualBackgroundSettings()
  const [backgroundType, setBackgroundType] = useState(initialBackgroundType)

  const [videoEnabled, setVideoEnabled] = useState(userChoices.videoEnabled)
  const [videoDeviceId, setVideoDeviceId] = useState(userChoices.videoDeviceId)

  const { data, loading } = useQuery(MeetingPrejoinRoomDataDocument, {
    variables: { id: Number(meetingId) },
    skip: !meetingId,
  })

  const {
    access: { locale },
  } = useCurrentSession()

  if (loading || pageDetailsLoading) {
    return <PrejoinSkeleton />
  }

  return (
    <>
      <F0Dialog
        isOpen={!openPrivacyPolicy}
        onClose={() =>
          navigate(
            navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
              meetingId: Number(meetingId),
              employeeId: Number(employeeId),
            })
          )
        }
        title={
          data?.meetings.meeting?.title
            ? `${data.meetings.meeting.title} · ${formatDateToString(
                data.meetings.meeting.startTime,
                locale
              )} - ${formatTimeToString(data.meetings.meeting.startTime, locale)}`
            : i18n.t('meetings.prejoin.title')
        }
        secondaryAction={{
          onClick: () =>
            navigate(
              navigation.engagement.one_on_ones.employeeId.meetingId.record.fullPath({
                meetingId: Number(meetingId),
                employeeId: Number(employeeId),
              })
            ),
          label: i18n.t('meetings.prejoin.room_info.presential_button'),
        }}
        primaryAction={{
          onClick: () =>
            navigate(
              navigation.engagement.one_on_ones.employeeId.meetingId.room.fullPath({
                meetingId: Number(meetingId),
                employeeId: Number(employeeId),
              })
            ),
          label: i18n.t('meetings.prejoin.room_info.enter_meeting_button'),
        }}
      >
        <Box gap={'s12'}>
          <PrejoinVideoContainer
            openPrivacyPolicy={openPrivacyPolicy}
            loading={loading || pageDetailsLoading}
            videoEnabled={videoEnabled}
            videoDeviceId={videoDeviceId}
            backgroundType={backgroundType || 'blur'}
          />
          <PrejoinControlBar
            onVideoEnabledChange={setVideoEnabled}
            onVideoDeviceIdChange={setVideoDeviceId}
            onBackgroundTypeChange={setBackgroundType}
          />
        </Box>
        <Box flexDirection="row" gap="s6" style={{ marginTop: '16px' }}>
          <Text.Normal size="200">{i18n.t('meetings.prejoin.privacy.description')}</Text.Normal>
          <Link.Touchable type="default" onClick={() => setOpenPrivacyPolicy(true)}>
            {i18n.t('meetings.prejoin.privacy.link')}
          </Link.Touchable>
        </Box>
      </F0Dialog>

      <PrivacyModal onOpen={openPrivacyPolicy} onClose={() => setOpenPrivacyPolicy(false)} />
    </>
  )
}

export default MeetingPrejoinRoomPage
